import { watch, nextTick } from 'vue';

const EASINGS = {
    linear: t => t,
    easeInOutCubic: t => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2),
    easeOutCubic: t => 1 - Math.pow(1 - t, 3),
    easeOutQuart: t => 1 - Math.pow(1 - t, 4),
};

function prefersReducedMotion() {
    return typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function blockOffset(block, elementHeight, viewportHeight) {
    switch (block) {
        case 'start': return 0;
        case 'end': return viewportHeight - elementHeight;
        case 'center':
        default: return (viewportHeight - elementHeight) / 2;
    }
}

function animateScrollTo(targetY, duration, easingName) {
    const startY = window.scrollY;
    const distance = targetY - startY;
    if (Math.abs(distance) < 1) return;

    const easing = EASINGS[easingName] || EASINGS.easeInOutCubic;
    const startTime = performance.now();

    function step(now) {
        const elapsed = now - startTime;
        const t = Math.min(elapsed / duration, 1);
        window.scrollTo({ top: startY + distance * easing(t), left: 0, behavior: 'instant' });
        if (t < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
}

/**
 * useScrollOnToggle — cuando un toggle/ref pasa de false a true, hace scroll
 * al elemento target para que la sección recién revelada quede alineada.
 *
 * Por defecto usa una animación JS con `requestAnimationFrame` y curva
 * `easeInOutCubic` durante 600ms (más controlada que el `scrollIntoView`
 * nativo, que no expone duración). Si pasas `behavior: 'auto' | 'instant'`,
 * se delega al API nativa (útil para tests).
 *
 * Respeta `prefers-reduced-motion: reduce` saltando la animación.
 *
 * @param {Function} getter  Función que devuelve el valor a observar (ej: () => localData.descuento_abonado).
 * @param {Object}   target  Ref al elemento DOM destino (debe ser un HTMLElement).
 * @param {Object}   [options]
 * @param {string}   [options.block='center']      start | center | end.
 * @param {number}   [options.offset=100]          Px que el elemento se desplaza hacia el TOP desde la posición del block (positivo = más arriba del block; p.ej. block:'center' + offset:100 → elemento 100px por encima del centro).
 * @param {string}   [options.behavior='smooth']   smooth | auto | instant.
 * @param {number}   [options.duration=600]        Duración en ms (ignorado si behavior !== 'smooth').
 * @param {string}   [options.easing='easeInOutCubic']  linear | easeInOutCubic | easeOutCubic | easeOutQuart.
 */
export function useScrollOnToggle(getter, target, options = {}) {
    const {
        block = 'center',
        offset = 100,
        behavior = 'smooth',
        duration = 600,
        easing = 'easeInOutCubic',
        enabled = () => true
    } = options;

    watch(getter, (newVal, oldVal) => {
        // Optional gate: when the caller provides `enabled` and it returns
        // false at fire time, this toggle event scrolls nothing (the toggle
        // itself still happens — only the scroll is suppressed). This lets
        // programmatic activations (e.g. selecting an SGA event auto-enables
        // nominativas) skip the scroll, while user clicks keep it.
        if (newVal && !oldVal && enabled()) {
            nextTick(() => {
                const el = target.value;
                if (!el) return;

                if (behavior === 'auto' || behavior === 'instant') {
                    el.scrollIntoView({ behavior, block });
                    return;
                }

                if (prefersReducedMotion()) {
                    el.scrollIntoView({ behavior: 'auto', block });
                    return;
                }

                const rect = el.getBoundingClientRect();
                const targetY = window.scrollY + rect.top
                    - blockOffset(block, rect.height, window.innerHeight)
                    + offset;
                animateScrollTo(targetY, duration, easing);
            });
        }
    });
}